<template>
  <div class="text-left">
    <h5 class="text-center">
      <strong>{{ $t("form.title") }}:</strong> {{ title }}
      <span class="ml-5" v-if="supervisor"
        ><strong> Supervisor:</strong> {{ supervisor.username }}</span
      >
    </h5>
    <v-divider />
    <v-form ref="form">
      <v-stepper v-model="step">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="step > 1" step="1">{{
            $t("project.basicData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="2" :complete="step > 2">{{
            $t("project.consortiumData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="3" :complete="step > 3">{{
            $t("project.subprojectsData")
          }}</v-stepper-step>

          <v-divider></v-divider>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.title" :label="$t('form.project.title')" entity-type="project" field="title" readonly />
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.acronym") }}
            </h5>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="project.acronym"
                  :label="$t('form.project.acronym')"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <h5>
                  {{ $t("type") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-select
                      v-model="project.type"
                      :items="projectTypes"
                      item-value="value"
                      item-text="text"
                      :label="$t('type')"
                      outlined
                      readonly
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6">
                <h5>
                  {{ $t("form.project.external") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-checkbox
                      v-model="project.external"
                      :label="$t('form.project.external_more')"
                      readonly
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <h5>
                  {{ $t('form.doi') }}
                </h5>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="project.doi"
                      :label="$t('form.doi')"
                      outlined
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6">
                <h5>
                  {{ $t('form.projectcode') }}
                </h5>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="project.code"
                      :label="$t('form.projectcode')"
                      outlined
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.dates") }}
            </h5>
            <v-row>
              <v-col cols="6" class="py-0">
                <v-text-field
                  v-model="project.start_date"
                  :label="$t('form.project.start_date')"
                  prepend-inner-icon="event"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="6" class="py-0">
                <v-text-field
                  v-model="project.end_date"
                  :label="$t('form.project.end_date')"
                  prepend-inner-icon="event"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.description" :label="$t('form.description')" entity-type="project" field="description" rich readonly />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.objectives" :label="$t('form.project.objectives')" entity-type="project" field="objectives" rich readonly />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.results" :label="$t('form.project.results')" entity-type="project" field="results" rich readonly />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.main_citius_investigators") }}</h5>
                <SelectPeopleList
                  :disabled-form="true"
                  :items-selected="project.main_citius_investigators"
                />
              </v-col>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.citius_investigators") }}</h5>
                <SelectPeopleList
                  :disabled-form="true"
                  :items-selected="project.citius_investigators"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.main_external_investigators") }}</h5>
                <v-row>
                  <v-col>
                    <ArrayFieldForm
                      v-model="project.main_external_investigators"
                      :read-only="true"
                      name-field="main_external_investigators"
                      :label="$t('form.project.main_external_investigators')"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.external_investigators") }}</h5>
                <v-row>
                  <v-col>
                    <ArrayFieldForm
                      v-model="project.external_investigators"
                      :read-only="true"
                      name-field="external_investigators"
                      :label="$t('form.project.external_investigators')"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.files") }}
            </h5>
            <UploadFiles
              :read-only="true"
              object_type="project"
              :id="Number(this.$route.params.id)"
              :final="false"
            />
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.funder_programs") }}
                </h5>
                <v-row>
                  <v-col>
                    <SelectPeopleList
                      :disabled-form="true"
                      :items-selected="project.funder_programs"
                      endpoint="funderProgram"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <MultilingualTextField v-if="load_complete" v-model="project.funder_text" :label="$t('form.funder_text')" entity-type="project" field="funder_text" rich readonly />
            <ProjectBudgetFields :total-budget.sync="project.total_budget" :citius-budget.sync="project.citius_budget" readonly />
          <v-row>
            <v-col>
              <h5>
                {{ $t("form.project.additional_text") }}
              </h5>
              <v-text-field
                v-model="project.additional_text"
                :label="$t('form.project.additional_text')"
                :readonly="true"
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.contractor") }}
                </h5>
                <v-autocomplete
                  v-model="project.contractor"
                  :label="$t('form.project.contractor')"
                  :readonly="true"
                  :items="organizations"
                  item-value="id"
                  item-text="name"
                  return-object
                  outlined
                ></v-autocomplete>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.image") }}
            </h5>
            <v-row>
              <v-col>
                <v-img
                  :src="this.$store.state.endpoint + project.image"
                  :max-width="500"
                  :max-height="300"
                ></v-img>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.uri") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="project.uri"
                      :label="$t('form.project.uri')"
                      readonly
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.publication.research_areas") }}
            </h5>
            <v-row>
              <v-col cols="12" class="py-0">
                <v-select
                  :items="researchAreas"
                  v-model="project.research_areas"
                  :label="$t('form.publication.research_areas')"
                  required
                  :item-text="'name.' + $i18n.locale"
                  item-value="id"
                  readonly
                  multiple
                  outlined
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-row>
              <v-col>
                <v-checkbox
                  v-model="has_consortium"
                  :label="$t('form.project.has_consortium')"
                  readonly
                />
              </v-col>
            </v-row>
            <div v-if="has_consortium">
              <h5>
                {{ $t("form.project.consortium_dates") }}
              </h5>
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-text-field
                    v-model="project.consortium.start_date"
                    :label="$t('form.project.start_date')"
                    prepend-inner-icon="event"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-text-field
                    v-model="project.consortium.end_date"
                    :label="$t('form.project.end_date')"
                    prepend-inner-icon="event"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-0">
                  <v-checkbox
                    indeterminate
                    v-model="project.consortium.is_citius"
                    :label="$t('form.project.consortium_citius')"
                    readonly
                  />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.leader") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-text-field
                    v-if="project.consortium.leader.name !== undefined"
                    v-model="project.consortium.leader.name"
                    :label="$t('form.project.leader')"
                    readonly
                    outlined
                  />
                  <v-select
                    v-else
                    v-model="project.consortium.leader"
                    :items="organizations"
                    item-value="id"
                    item-text="name"
                    :label="$t('form.project.leader')"
                    outlined
                    readonly
                  ></v-select>
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.partners") }}
              </h5>
              <v-row>
                <v-col>
                  <SelectPeopleList
                    :disabled-form="true"
                    :items-selected="project.consortium.partners"
                    endpoint="organization"
                  />
                </v-col>
              </v-row>
            </div>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="3">
            <div
              v-for="(subproject, index) in project.subprojects"
              :key="index"
            >
              <h4 class="text-left mb-5">
                {{ $t("form.project.subproject") }} {{ index + 1 }}
              </h4>

              <v-row>
                <v-col cols="12" class="py-0">
                  <MultilingualTextField v-if="load_complete" v-model="subproject.title" :label="$t('form.project.title')" entity-type="project" field="subproject_title" readonly />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.subproject_dates") }}
              </h5>
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-text-field
                    v-model="subproject.start_date"
                    :label="$t('form.project.start_date')"
                    prepend-inner-icon="event"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-text-field
                    v-model="subproject.end_date"
                    :label="$t('form.project.end_date')"
                    prepend-inner-icon="event"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>


              <v-row>
                <v-col cols="12" class="py-0">
                  <MultilingualTextField v-if="load_complete" v-model="subproject.description" :label="$t('form.description')" entity-type="project" field="subproject_description" rich readonly />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.main_citius_investigators") }}</h5>
                  <v-autocomplete
                          v-model="subproject.main_citius_investigators"
                          :items="namePersons"
                          item-value="id"
                          item-text="name"
                          readonly
                          outlined
                          multiple
                          :label="$t('form.project.main_citius_investigators')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.citius_investigators") }}</h5>
                  <v-autocomplete
                          v-model="subproject.citius_investigators"
                          :items="namePersons"
                          item-value="id"
                          item-text="name"
                          readonly
                          outlined
                          multiple
                          :label="$t('form.project.citius_investigators')"
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.main_external_investigators") }}</h5>
                  <v-row>
                    <v-col>
                      <ArrayFieldForm
                        v-model="subproject.main_external_investigators"
                        :read-only="true"
                        name-field="main_external_investigators"
                        :label="$t('form.project.main_external_investigators')"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.external_investigators") }}</h5>
                  <v-row>
                    <v-col>
                      <ArrayFieldForm
                        v-model="subproject.external_investigators"
                        :read-only="true"
                        name-field="external_investigators"
                        :label="$t('form.project.external_investigators')"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-divider />
            </div>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
      <v-divider />

      <DirectorActions
        v-if="isAuthorize"
        :object_type="object_type"
        class="text-center"
      />
      <SupervisorActions
        v-else
        :item="project"
        :object_type="object_type"
        :form_name="form_name"
        :state="state"
        :supervisor="supervisor !== null ? supervisor.username : ''"
        @change-state-supervisor="onStateSupervisorChange"
      />
    </v-form>
  </div>
</template>

<script>
import axios from "axios";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import SelectPeopleList from "../../reusables/SelectPeopleList";
import project_data from "../../../mixins/project_data";
import project_model from "../../../mixins/model_utils/project_model";
import DirectorActions from "../../action_buttons/DirectorActions";
import SupervisorActions from "../../action_buttons/SupervisorActions";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import ProjectBudgetFields from "@/components/projects/ProjectBudgetFields.vue";
import UploadFiles from "../../reusables/UploadFiles";

export default {
  mixins: [project_data, project_model],
  components: {
    UploadFiles,
    ArrayFieldForm,
    SelectPeopleList,
    MultilingualTextField,
    ProjectBudgetFields,
    DirectorActions,
    SupervisorActions
  },

  data: () => ({
    form_name: "newProjectForm",
    step: 1,
    tab: null,
    tab_title: 0,
    tab_description: 0,
    tab_objectives: 0,
    tab_results: 0,
    load_complete: false,
    tabs_subprojects: [],
    menu_start_date: false,
    menu_end_date: false,
    menu_consortium_start_date: false,
    menu_consortium_end_date: false,
    has_consortium: false,
    state: "",
    title: "",
    reject_comment: "",
    object_id: null,
    object_type: "project",
    supervisor: ""
  }),

  computed: {
    isAuthorize() {
      return this.$route.meta.authorize !== undefined;
    }
  },

  created() {
    this.getResearchAreas();
    this.getNamePersons();
    this.getProjectTypes();
    this.getOrganizations();
    this.getData();
  },

  methods: {
    getData() {
      let path =
        this.$store.state.endpoint +
        "/moderatedOperations/review/project/" +
        this.$route.params.id;
      axios
        .get(path)
        .then(response => {
          this.project = response.data.data.user.project;
          this.addEmptyDataMultiLanguage();
          if (this.project.consortium !== undefined) {
            this.has_consortium = true;
          }
          this.title = response.data.title;
          this.state = response.data.state;
          this.reject_comment = response.data.reject_comment;
          this.project.id = response.data.object_id;
          this.project.image = response.data.image;
          this.form_name = response.data.form_name;
          this.title = response.data.title;
          this.state = response.data.state;
          this.supervisor = response.data.supervisor;
          this.object_type = response.data.object_type;
          this.project.subprojects.forEach(subproject => {
            this.tabs_subprojects.push({
              tab_title: 0,
              tab_description: 0
            });
          });
        })
        .finally(() => (this.load_complete = true))
        .catch(error => {
          console.log(error);
        });
    },

    onStateSupervisorChange(state, supervisor) {
      this.state = state;
      this.supervisor = supervisor;
    }
  }
};
</script>
